/**
 * Efterfrågan och ursprung, mätt av oss själva (DemandEvent, Booking.attribution).
 * Underlaget för att styra annonsbudgeten efter beläggning: hur många som tittade
 * på en dag och gick, vilken kanal en bokning kom från, och telefonklick.
 *
 * Integritet, samma hållning som consent-log.ts:
 * - `flowId` är ett slumpat id som bara lever i sidans minne. Ingen cookie, ingen
 *   lagring; en omladdning ger ett nytt. Det knyter ett /boka-besök till sin
 *   bokning, inte till en person.
 * - utm_*, referrer-värd och landningssida bärs alltid: samma värden för alla som
 *   klickade på samma annons, ingen identifierare. De hålls i minnet från första
 *   sidan och följer med i URL:en som `uka` (ClickIdPassthrough), eftersom
 *   bokningsknapparna laddar om sidan. Ingen lagring.
 * - Klick-id (gclid, gbraid, wbraid, oppref) identifierar ett annonsklick och
 *   skickas bara med marknadsföringssamtycke. Först då sparas ursprunget också i
 *   sessionStorage.
 */
import { hasConsent } from "./consent";

const STORAGE_KEY = "uk_attribution";
const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"] as const;
const CLICK_ID_KEYS = ["gclid", "gbraid", "wbraid", "oppref"] as const;
/** URL-parametern som bär landningen vidare genom omladdningar (ClickIdPassthrough). */
export const LANDING_PARAM = "uka";
const LANDING_KEYS = [...UTM_KEYS, "referrerHost", "landingPath", "landedAt"] as const;

export interface Attribution {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
  referrerHost?: string;
  landingPath?: string;
  landedAt?: string;
  gclid?: string;
  gbraid?: string;
  wbraid?: string;
  oppref?: string;
  /** Samtycket när ursprunget skickades. */
  marketingConsent?: boolean;
}

let landing: Attribution | null = null;
let flow: string | null = null;

const clip = (v: string | null | undefined, max = 200) => (v ? v.slice(0, max) : undefined);

function decodeLanding(raw: string): Attribution | null {
  try {
    const json = decodeURIComponent(escape(atob(raw.replace(/-/g, "+").replace(/_/g, "/"))));
    const parsed = JSON.parse(json) as Record<string, unknown>;
    const out: Attribution = {};
    for (const k of LANDING_KEYS) {
      const v = parsed[k];
      if (typeof v === "string" && v) out[k] = clip(v);
    }
    return out;
  } catch {
    return null;
  }
}

/** Landningen (utan klick-id) som URL-säkert värde för `uka`; null utan källa. */
export function landingParam(): string | null {
  const l = captureLanding();
  if (!UTM_KEYS.some((k) => l[k]) && !l.referrerHost) return null;
  const data: Record<string, string> = {};
  for (const k of LANDING_KEYS) if (l[k]) data[k] = l[k]!;
  return btoa(unescape(encodeURIComponent(JSON.stringify(data))))
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
}

function captureLanding(): Attribution {
  if (landing) return landing;
  const out: Attribution = {};
  if (typeof window === "undefined") return out;
  const params = new URLSearchParams(window.location.search);
  // Omladdning inom besöket: landningen kom med i URL:en.
  const carried = params.get(LANDING_PARAM);
  const fromUrl = carried ? decodeLanding(carried) : null;
  if (fromUrl) Object.assign(out, fromUrl);
  for (const k of fromUrl ? CLICK_ID_KEYS : [...UTM_KEYS, ...CLICK_ID_KEYS]) {
    const v = clip(params.get(k));
    if (v) out[k] = v;
  }
  if (!fromUrl) {
    try {
      const ref = document.referrer ? new URL(document.referrer).hostname : "";
      if (ref && ref !== window.location.hostname) out.referrerHost = ref;
    } catch {
      // ogiltig referrer
    }
    out.landingPath = clip(window.location.pathname);
    out.landedAt = new Date().toISOString();
  }

  // En omladdning mitt i besöket: ta det som sparades när samtycket fanns.
  const hasSource = UTM_KEYS.some((k) => out[k]) || out.referrerHost;
  if (!hasSource) {
    try {
      const saved = sessionStorage.getItem(STORAGE_KEY);
      if (saved) return (landing = JSON.parse(saved) as Attribution);
    } catch {
      // ingen lagring
    }
  }
  return (landing = out);
}

/** gclid ur Googles conversion linker-cookie (_gcl_aw = "GCL.<tid>.<gclid>"). */
function gclidFromCookie(): string | undefined {
  const m = document.cookie.match(/(?:^|; )_gcl_aw=([^;]*)/);
  const parts = m ? decodeURIComponent(m[1]).split(".") : [];
  return parts.length >= 3 ? clip(parts.slice(2).join(".")) : undefined;
}

/** Ursprunget som det får skickas just nu: klick-id bara med marknadsföringssamtycke. */
export function currentAttribution(): Attribution {
  const base = { ...captureLanding() };
  const marketing = hasConsent("marketing");
  if (marketing) {
    if (!base.gclid) base.gclid = gclidFromCookie();
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(base));
    } catch {
      // ingen lagring
    }
  } else {
    for (const k of CLICK_ID_KEYS) delete base[k];
  }
  return { ...base, marketingConsent: marketing };
}

/** Id för det här /boka-flödet. Bara i minnet. */
export function demandFlowId(): string {
  if (!flow) {
    flow =
      typeof crypto !== "undefined" && "randomUUID" in crypto
        ? crypto.randomUUID()
        : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
  }
  return flow;
}

/** Efter en bokning börjar nästa besök på ett nytt flöde. */
export function resetDemandFlow(): void {
  flow = null;
}

export type DemandClientEvent =
  | { type: "phone_click"; number: string }
  | { type: "step_view"; step: string; serviceType?: string; areaSlug?: string | null; volume?: string | null }
  | {
      type: "date_selected" | "time_selected";
      /** Always set for date_selected; time_selected may come before the date. */
      targetDate?: string;
      time?: string | null;
      serviceType?: string;
      areaSlug?: string | null;
      volume?: string | null;
    };

/** Skickar en händelse till /api/demand-event. Tyst vid fel. */
export function trackDemand(event: DemandClientEvent): void {
  if (typeof window === "undefined") return;
  const body = JSON.stringify({
    ...event,
    flowId: event.type === "phone_click" ? (flow ?? undefined) : demandFlowId(),
    path: window.location.pathname,
    attribution: currentAttribution(),
  });
  try {
    const blob = new Blob([body], { type: "application/json" });
    if (navigator.sendBeacon?.("/api/demand-event", blob)) return;
  } catch {
    // faller tillbaka på fetch
  }
  fetch("/api/demand-event", {
    method: "POST",
    body,
    keepalive: true,
    headers: { "Content-Type": "application/json" },
  }).catch(() => {});
}

// Fånga landningen så fort modulen laddas (PhoneClickTracker i layouten).
if (typeof window !== "undefined") captureLanding();
